/**
 * DashboardPage — the Quarterly Review page (handoff §3).
 *
 * Composition (top to bottom):
 *   - PageBanner       (clinic switcher, meeting status line)
 *   - PracticeMeetingCard
 *   - ScoreCardsRow    (5 KpiCards: Quality, IA, PI, Cost, Projected total)
 *   - ScoreTrajectoryChart
 *   - MeasureGrid      (the workhorse SchemaDataGrid)
 *   - DashboardFooter  (compliance strip + 3 action buttons)
 *
 * Each section is a child component. The page itself is responsible for
 * the dashboard query, URL → state parsing, and the loading / error /
 * no-snapshot edge cases enumerated in handoff §9.
 *
 * Hooks-at-top rule: every hook in this component runs on every render
 * before any conditional return.
 */
import { useParams } from "react-router-dom";
import { Spinner } from "@tensaw/design-system";
import { useDashboard } from "@/hooks/useDashboard";
import { AccessDenied } from "@/auth/AccessDenied";
import { isAccessDenied } from "@/auth/errors";
import { PageBanner } from "./dashboard/PageBanner";
import { PracticeMeetingCard } from "./dashboard/PracticeMeetingCard";
import { ScoreCardsRow } from "./dashboard/ScoreCardsRow";
import { ScoreTrajectoryChart } from "./dashboard/ScoreTrajectoryChart";
import { MeasureGrid } from "./dashboard/MeasureGrid";
import { DashboardFooter } from "./dashboard/DashboardFooter";
import { buildPinnedContext } from "./dashboard/types";

/**
 * Parse a router param to an integer. Returns NaN for missing/invalid
 * input — callers should check `Number.isFinite` before using.
 */
function parseIntParam(s: string | undefined): number {
  if (s === undefined) return Number.NaN;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : Number.NaN;
}

export function DashboardPage() {
  const { clinicId: clinicIdParam, year: yearParam, meetingId: meetingIdParam } = useParams();

  const clinicId = parseIntParam(clinicIdParam);
  // Default to current year if not in URL. (URL form: /clinics/87326 omits year.)
  const yearFromUrl = parseIntParam(yearParam);
  const year = Number.isFinite(yearFromUrl) ? yearFromUrl : new Date().getFullYear();
  const meetingIdFromUrl = parseIntParam(meetingIdParam);
  const meetingId = Number.isFinite(meetingIdFromUrl) ? meetingIdFromUrl : undefined;

  const dashboardQ = useDashboard(clinicId, year, meetingId);

  // 404-ish: clinicId wasn't in the URL or didn't parse.
  if (!Number.isFinite(clinicId)) {
    return (
      <div className="rounded-lg border border-border bg-white p-8 text-center">
        <h2 className="text-lg font-semibold text-gray-800">Clinic not found</h2>
        <p className="mt-2 text-sm text-gray-600">
          The URL is missing a valid clinic id. Pick a clinic from the list.
        </p>
      </div>
    );
  }

  if (dashboardQ.isLoading) {
    return (
      <div className="flex h-96 items-center justify-center" role="status" aria-label="Loading">
        <Spinner />
      </div>
    );
  }

  // V1.3.0: 403 AccessDeniedError gets a distinct UI (not a generic
  // "failed to load"). The user's session is valid; the resource just
  // isn't in their scope. The retry button would just 403 again — show
  // a path back instead.
  if (isAccessDenied(dashboardQ.error)) {
    return <AccessDenied clinicId={clinicId} />;
  }

  if (dashboardQ.isError || !dashboardQ.data) {
    const message = dashboardQ.error instanceof Error
      ? dashboardQ.error.message
      : "Failed to load dashboard.";
    return (
      <div className="rounded-lg border border-border bg-white p-8">
        <h2 className="text-lg font-semibold text-gray-800">Couldn't load this dashboard</h2>
        <p className="mt-2 text-sm text-gray-600">{message}</p>
        <button
          type="button"
          onClick={() => void dashboardQ.refetch()}
          className="mt-4 inline-flex items-center rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover"
        >
          Retry
        </button>
      </div>
    );
  }

  const data = dashboardQ.data;
  // Compute the pinned context once. Non-null iff the backend returned
  // `snapshot.pinned_to_meeting: true` (added in v0.3.1). Every section
  // that mutates passes this down to disable affordances when set.
  const pinned = data.snapshot.pinned_to_meeting
    ? buildPinnedContext(data.meeting ?? null, data.snapshot.pinned_as_of, year)
    : null;

  return (
    <div className="flex flex-col gap-4">
      <PageBanner
        clinicId={clinicId}
        year={year}
        clinic={data.clinic}
        meeting={data.meeting ?? null}
        pinned={pinned}
      />
      <PracticeMeetingCard
        clinicId={clinicId}
        year={year}
        clinic={data.clinic}
        meeting={data.meeting ?? null}
        snapshot={data.snapshot}
        pinned={pinned}
      />
      <ScoreCardsRow
        clinicId={clinicId}
        year={year}
        scoreCards={data.score_cards}
        pinned={pinned}
      />
      <ScoreTrajectoryChart
        trajectory={data.trajectory}
        goalTotal={data.score_cards.goal_total ?? null}
        pinned={pinned}
      />
      <MeasureGrid
        clinicId={clinicId}
        year={year}
        measures={data.measures}
        rollup={data.rollup}
        hasSnapshot={data.snapshot.snapshot_date !== null && data.snapshot.snapshot_date !== undefined}
        pinned={pinned}
      />
      <DashboardFooter
        clinicId={clinicId}
        year={year}
        meeting={data.meeting ?? null}
      />
    </div>
  );
}
